import type {
  ColDef,
  ColGroupDef,
  GridOptions,
  ValueGetterParams,
} from '@ag-grid-community/core';
import {
  DeviceLastActiveTimeframe,
  DeviceStateAnalysisAsset,
} from '@cdw-ae/common-data';
import type {
  FilterBarField,
  SelectedFilterField,
} from '@cdw-ae/filter-bar-interfaces';
import { map, Observable } from 'rxjs';
import { DevicesDataService } from '../../../../../common/services/device/devices-data.service';
import { filterToSupportedStateAnalysisFields, StateAnalysisReportFilterSettings } from '../../../../../common/services/route-config/configs/filter-bar-config.config';
import { OuPathService } from '../../../../../common/services/ou-path/ou-path.service';
import { createFifteenDayActivityReportConfig } from '../configs/fifteen-day-activity-report.config';
import type { ReportData } from '../report-viewer/report-viewer.model';
import { BaseReportHandler } from './base-report.handler';

interface FifteenDayActivitySummary {
  ouPath: string;
  status: string;
  activeDevices: number;
  totalDaysUsed: number;
  totalMinutesUsed: number;
  totalDevices: number;
  devicesNotUsed: number;
  devicesNoPolicySync: number;
  avgDaysUsed?: number;
  avgMinutesUsed?: number;
}

/**
 * Handler for 15-Day Activity by OU aggregate reports
 * Manages filtering, grid configuration, and data processing for 15-Day Activity reports
 */
export class FifteenDayActivityReportHandler extends BaseReportHandler {
  /**
   * Static method to load 15-Day Activity report data from API
   * Fetches device state analysis data, processes it with filters, and returns configured report data
   * @param devicesDataService Service to fetch device data
   * @param filters Optional filters to apply to the data
   * @returns Observable of ReportData ready for display
   */
  static load(
    devicesDataService: DevicesDataService,
    filters?: SelectedFilterField[]
  ): Observable<ReportData> {
    // Get filters to apply (use provided filters or initial filters)
    const allFilters =
      filters || FifteenDayActivityReportHandler.getInitialFilters() || [];

    // Filter to only include fields supported by the state-analysis endpoint
    const filtersToApply = filterToSupportedStateAnalysisFields(allFilters);

    return devicesDataService.getDeviceStateAnalysis(filtersToApply).pipe(
      map((deviceStateAnalysisResponse) => {
        if (!deviceStateAnalysisResponse.success) {
          throw new Error(
            deviceStateAnalysisResponse.error ||
              'Failed to load 15-day activity report data'
          );
        }

        const processedData = FifteenDayActivityReportHandler.processRawData(
          deviceStateAnalysisResponse.data,
          filtersToApply
        );

        // Create and return report config with processed data
        return createFifteenDayActivityReportConfig(processedData);
      })
    );
  }

  getColumnDefs(): (ColDef | ColGroupDef)[] {
    const config = this.reportData.aggregateConfig;
    if (!config) return [];

    // Add hidden columns for aggregation
    const hiddenColumns: ColDef[] = [
      { field: 'totalDaysUsed', aggFunc: 'sum', hide: true },
      { field: 'totalMinutesUsed', aggFunc: 'sum', hide: true },
      { field: 'totalDevices', aggFunc: 'sum', hide: true },
    ];

    // Skip the first column (group hierarchy) and add aggregation to remaining columns
    const visibleColumns = config.columns.slice(1).map((col) => {
      if ('field' in col && col.field) {
        // Add aggregation for numeric count columns (sum them up for parent rows)
        const sumFields = [
          'activeDevices',
          'devicesNotUsed',
          'devicesNoPolicySync',
        ];

        if (sumFields.includes(col.field)) {
          return {
            ...col,
            aggFunc: 'sum',
          };
        }

        // For average fields, calculate from summed totals
        const avgFields = ['avgDaysUsed', 'avgMinutesUsed'];
        if (avgFields.includes(col.field)) {
          const fieldName = col.field;
          const totalField =
            fieldName === 'avgDaysUsed' ? 'totalDaysUsed' : 'totalMinutesUsed';

          return {
            ...col,
            valueGetter: (params: ValueGetterParams) => {
              // Leaf node - show stored value
              if (!params.node?.group) {
                return params.data?.[fieldName];
              }

              // Parent node - calculate from aggregated values
              const aggData = params.node.aggData;
              const totalUsage = aggData?.[totalField] || 0;
              const totalDevices = aggData?.totalDevices || 0;

              if (totalDevices === 0) return 0;
              return Math.round((totalUsage / totalDevices) * 10) / 10;
            },
          };
        }
      }
      return col;
    });

    return [...hiddenColumns, ...visibleColumns];
  }

  getGridOptions(): GridOptions {
    const config = this.reportData.aggregateConfig;
    if (!config) return {};

    return {
      treeData: true,
      animateRows: true,
      groupDefaultExpanded: 0, // Start collapsed
      getDataPath: (data: Record<string, unknown>) => {
        const ouPath = String(data.ouPath || '');
        return OuPathService.parsePathForGrid(ouPath);
      },
      autoGroupColumnDef: {
        headerName: config.columns[0]?.headerName || 'Group',
        flex: 3,
        cellRendererParams: {
          suppressCount: true,
        },
      },
      getChildCount: (data: Record<string, unknown>) => {
        return data[config.aggregationField] as number;
      },
      defaultColDef: {
        resizable: true,
        sortable: true,
      },
    };
  }

  static override getFilterBarSettings(): FilterBarField[] {
    return StateAnalysisReportFilterSettings;
  }

  static override getInitialFilters(): SelectedFilterField[] | null {
    return BaseReportHandler.getDefaultActiveStatusFilter(StateAnalysisReportFilterSettings);
  }

  /**
   * Process raw DeviceStateAnalysisAsset data into 15-day activity format
   * Aggregates data by organizational unit and last active timeframe
   */
  static override processRawData(
    rawData: unknown[],
    _filters: SelectedFilterField[]
  ): unknown[] {
    const deviceData = rawData as DeviceStateAnalysisAsset[];

    // Aggregate activity data by OU
    const activitySummaryLookupByOu = deviceData.reduce(
      (accumulator, currentValue) => {
        const ouPath = currentValue.groupingValue || 'Unknown OU';

        if (!accumulator[ouPath]) {
          accumulator[ouPath] = {
            ouPath,
            status: currentValue.status,
            activeDevices: 0,
            totalDaysUsed: 0,
            totalMinutesUsed: 0,
            totalDevices: 0,
            devicesNotUsed: 0,
            devicesNoPolicySync: 0,
          };
        }

        // Count devices by activity timeframe
        const timeframe = currentValue.deviceLastActiveTimeframe;

        switch (timeframe) {
          case DeviceLastActiveTimeframe.SevenDays:
          case DeviceLastActiveTimeframe.FourteenDays:
            accumulator[ouPath].activeDevices += currentValue.deviceCount;
            break;
          case DeviceLastActiveTimeframe.NotActive:
          case DeviceLastActiveTimeframe.ThirtyDays:
            accumulator[ouPath].devicesNotUsed += currentValue.deviceCount;
            break;
          case DeviceLastActiveTimeframe.Unknown:
            accumulator[ouPath].devicesNoPolicySync += currentValue.deviceCount;
            break;
        }

        // Sum total days and minutes used
        accumulator[ouPath].totalDaysUsed += currentValue.totalDaysUsed || 0;
        accumulator[ouPath].totalMinutesUsed +=
          currentValue.totalMinutesUsed || 0;

        return accumulator;
      },
      {} as Record<string, FifteenDayActivitySummary>
    );

    // Calculate averages from totals
    // AG Grid will automatically aggregate parent rows using aggFunc='sum'
    const rows = Object.values(activitySummaryLookupByOu).map((summary) => {
      const totalDevices =
        summary.activeDevices +
        summary.devicesNotUsed +
        summary.devicesNoPolicySync;

      // Calculate averages - return 0 if no devices
      const calculateAverage = (
        totalUsage: number,
        deviceCount: number
      ): number => {
        if (deviceCount === 0) return 0;
        return Math.round((totalUsage / deviceCount) * 10) / 10;
      };

      return {
        ...summary,
        totalDevices,
        avgDaysUsed: calculateAverage(summary.totalDaysUsed, totalDevices),
        avgMinutesUsed: calculateAverage(
          summary.totalMinutesUsed,
          totalDevices
        ),
      };
    });

    return OuPathService.redistributeNonLeafData(rows, [
      'activeDevices', 'devicesNotUsed', 'devicesNoPolicySync',
      'totalDaysUsed', 'totalMinutesUsed', 'totalDevices',
    ]);
  }
}
